import { useContext, useEffect, useMemo, useState } from 'react';

import { MapContext } from '../../../contexts/MapContext';
import { getFarmMeta } from '../../../utils/meta';
import { splitArrayByIndexes } from '../../../utils/utils';

/**
 * Pulls metadata about crops and sections.
 */
export const useFarmMeta = () => {
  const {
    fields: { fieldSections },
    crops: { availableCrops: cropMap, cropNameToId, cropVarietyIdToCropId },
    patterns: { inRowPatternsMap },
  } = useContext(MapContext);

  return useMemo(
    () =>
      getFarmMeta({
        fieldSections,
        cropsById: cropMap,
        cropNameToId,
        cropVarietyIdToCropId,
        inRowPatternsMap,
        includeHiddenSections: false,
        includeUnassignedStems: false,
      }),
    [
      fieldSections,
      cropMap,
      cropNameToId,
      cropVarietyIdToCropId,
      inRowPatternsMap,
    ],
  );
};

/**
 * Split up `array` of content into chunks, where `heights` is an array of the
 * heights of the items in `array`, and `chunkHeight` is the maximum height for
 * each chunk.
 *
 * Returns `chunks`, an array of arrays.
 */
export const useChunksByHeights = (array, heights, chunkHeight) => {
  const [splitIndexes, setSplitIndexes] = useState([]);

  useEffect(() => {
    const newSplitIndexes = [];

    let heightSum = 0;
    for (let i = 0; i < heights.length; i += 1) {
      heightSum += heights[i];

      if (heightSum > chunkHeight) {
        heightSum = heights[i];
        newSplitIndexes.push(i);
      }
    }

    setSplitIndexes(newSplitIndexes);
  }, [chunkHeight, heights]);

  const chunks = useMemo(() => splitArrayByIndexes(array, splitIndexes), [
    array,
    splitIndexes,
  ]);

  return chunks;
};
